/**
 * MSAL Bootstrap Service
 * Centralizes MSAL initialization and provides msalReady promise
 * All MSAL operations should await msalReady before proceeding
 */

import { PublicClientApplication, AccountInfo } from '@azure/msal-browser';
import { msalConfig, validateMsalConfig } from '../config/msal.config';
import { AUTH_API_CONFIG } from '../config/api.config';

// Create MSAL instance (but don't initialize yet)
let msalInstance: PublicClientApplication | null = null;

/**
 * Get MSAL instance (may be null if MSAL is not configured)
 */
export function getMsalInstance(): PublicClientApplication | null {
  return msalInstance;
}

/**
 * Select account from available accounts
 * Strategy (priority order):
 * 1. Persisted msal_last_account_id (most deterministic cross-refresh signal)
 * 2. Existing active account (if already set)
 * 3. Single account (if exactly one exists)
 * 4. First account (last resort)
 * 
 * Note: Redirect response account is handled separately before this function is called
 */
function selectAccount(accounts: AccountInfo[]): AccountInfo | null {
  if (accounts.length === 0) {
    return null;
  }

  // Priority 1: Prefer persisted homeAccountId (most deterministic cross-refresh signal)
  // This is the only deterministic signal in multi-account caches
  const storedAccountId = localStorage.getItem('msal_last_account_id');
  if (storedAccountId) {
    const found = accounts.find(acc => acc.homeAccountId === storedAccountId);
    if (found !== undefined) {
      return found;
    }
  }

  // Priority 2: If active account is already set and exists in accounts, prefer it
  if (msalInstance) {
    const activeAccount = msalInstance.getActiveAccount();
    if (activeAccount) {
      const found = accounts.find(
        (acc) => acc.homeAccountId === activeAccount.homeAccountId
      );
      if (found !== undefined) {
        return found;
      }
    }
  }

  // Priority 3: If exactly one account, use it (most common case)
  if (accounts.length === 1) {
    const account = accounts[0];
    return account ?? null;
  }

  // Priority 4: Multiple accounts: use first (deterministic but may need user selection UI)
  // In enterprise scenarios, you might want to show account picker
  const firstAccount = accounts[0];
  return firstAccount ?? null;
}

/**
 * MSAL Ready Promise
 * Completes only after:
 * 1. MSAL instance is created
 * 2. initialize() completes
 * 3. handleRedirectPromise() completes
 * 4. Active account is set (if accounts exist)
 * 
 * All MSAL operations should await this promise before proceeding
 */
export const msalReady: Promise<PublicClientApplication | null> = (async () => {
  // Only initialize if MSAL is configured
  if (AUTH_API_CONFIG.providerType !== 'msal' || !validateMsalConfig()) {
    return null;
  }

  // Check if Web Crypto API is available (required for MSAL)
  if (!window.isSecureContext || typeof crypto === 'undefined' || !crypto.subtle) {
    console.error('[MSAL Bootstrap] Web Crypto API not available. MSAL requires secure context (HTTPS or localhost).');
    return null;
  }

  try {
    // Create MSAL instance
    msalInstance = new PublicClientApplication(msalConfig);

    // Step 1: Initialize MSAL
    await msalInstance.initialize();

    // Step 2: Handle redirect promise (processes redirect responses and discovers accounts)
    // This is safe to call even if no redirect occurred (returns null)
    // However, if a redirect response exists, it is the most authoritative account signal
    try {
      const redirectResponse = await msalInstance.handleRedirectPromise();
      if (redirectResponse?.account) {
        // Redirect response exists - this is the most authoritative account
        msalInstance.setActiveAccount(redirectResponse.account);
        // Persist selected account for future refreshes
        localStorage.setItem('msal_last_account_id', redirectResponse.account.homeAccountId);
      }
    } catch (redirectError) {
      // Some redirect errors are real (invalid state, nonce mismatch, etc.) and indicate broken auth flow
      // Log them so we can detect corrupted state
      console.error('[MSAL Bootstrap] Redirect handling failed:', redirectError);
      
      // OPTIONAL FUTURE ENHANCEMENT: Auto-recover on state/nonce errors
      // If you ever see real state/nonce errors in production, you can upgrade this comment to auto-recovery:
      // if (isStateOrNonceError(redirectError)) {
      //   console.warn('[MSAL Bootstrap] State/nonce error detected, clearing cache for recovery');
      //   msalInstance.clearCache();
      // }
      // 
      // Helper function would be:
      // function isStateOrNonceError(error: any): boolean {
      //   const message = error?.message?.toLowerCase() || '';
      //   return message.includes('state') || message.includes('nonce') || message.includes('invalid_state');
      // }
      
      // For now, continue with getAllAccounts() fallback below
    }

    // Step 3: Restore active account from cache (if not already set from redirect)
    // CRITICAL: Do NOT restore if previous login failed - poisoned account state would be re-injected
    // and cause Entra to forward username hint to Google (which rejects it).
    const hasLoginError = typeof sessionStorage !== 'undefined' && sessionStorage.getItem('login_error') !== null;
    if (hasLoginError) {
      // Do NOT restore account - let the user retry with a clean slate
      return msalInstance;
    }

    const activeAccount = msalInstance.getActiveAccount();
    if (!activeAccount) {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        const selectedAccount = selectAccount(accounts);
        if (selectedAccount) {
          msalInstance.setActiveAccount(selectedAccount);
          // Persist selected account for future refreshes
          localStorage.setItem('msal_last_account_id', selectedAccount.homeAccountId);
        }
      }
    } else {
      // Active account already set (from redirect or previous session)
      // Ensure it's persisted for future refreshes
      localStorage.setItem('msal_last_account_id', activeAccount.homeAccountId);
    }

    return msalInstance;
  } catch (error) {
    console.error('[MSAL Bootstrap] Failed to initialize MSAL:', error);
    msalInstance = null;
    return null;
  }
})();
